import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const STYLE = {
  version: 8,
  sources: {
    road: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      maxzoom: 19,
      attribution: "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors",
    },
  },
  layers: [{ id: "road", type: "raster", source: "road" }],
};

const dot = (color, ring = false, glyph = "") => {
  const el = document.createElement("div");
  el.style.cssText = `width:${ring ? 30 : 22}px;height:${ring ? 30 : 22}px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:${ring ? `0 0 0 5px ${color}44` : "0 2px 5px rgba(0,0,0,.4)"};display:flex;align-items:center;justify-content:center;font-size:13px;line-height:1`;
  el.textContent = glyph;
  return el;
};

export function RiderMap({ store, dest, rider, route }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const mk = useRef({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const c = rider || dest || store || [45.46, 9.19];
    const map = new maplibregl.Map({
      container: ref.current,
      style: STYLE,
      center: [c[1], c[0]],
      zoom: 14,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.scrollZoom.disable();
    map.on("load", () => { map.resize(); setLoaded(true); });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; setLoaded(false); };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const set = (key, ll, make) => {
      if (!ll) { if (mk.current[key]) { mk.current[key].remove(); delete mk.current[key]; } return; }
      if (mk.current[key]) mk.current[key].setLngLat([ll[1], ll[0]]);
      else mk.current[key] = new maplibregl.Marker({ element: make() }).setLngLat([ll[1], ll[0]]).addTo(map);
    };
    set("store", store, () => dot("#FFC400", false, "🏪"));
    set("dest", dest, () => dot("#EF4444", false, "🏁"));
    set("rider", rider, () => dot("#2E90FA", true, "🛵"));
    const geo = { type: "Feature", geometry: { type: "LineString", coordinates: (route || []).map((p) => [p[1], p[0]]) } };
    if (map.getSource("route")) map.getSource("route").setData(geo);
    else {
      map.addSource("route", { type: "geojson", data: geo });
      map.addLayer({ id: "route", type: "line", source: "route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#2E90FA", "line-width": 5, "line-opacity": 0.85 } });
    }
    const pts = [store, dest, rider].filter(Boolean);
    if (pts.length > 1) {
      const b = new maplibregl.LngLatBounds();
      pts.forEach((p) => b.extend([p[1], p[0]]));
      map.fitBounds(b, { padding: 60, maxZoom: 16, duration: 700 });
    } else if (pts.length === 1) {
      map.easeTo({ center: [pts[0][1], pts[0][0]], zoom: 15, duration: 700 });
    }
  }, [loaded, JSON.stringify(store), JSON.stringify(dest), JSON.stringify(rider), JSON.stringify(route)]);

  return <div ref={ref} className="rounded-2xl overflow-hidden border border-white/10 shadow" style={{ height: 300 }} data-testid="rider-map" />;
}
